import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, Upload, FolderOpen, CreditCard, Users, MessageCircle, Settings, Bell, Plus, Trash2, Download, CheckCircle, XCircle, FileText, GraduationCap, Layers, LogOut, ChevronLeft, ChevronRight, Search, Send, Paperclip, Volume2, VolumeX, File, Image, X, HardDrive, Wand2, BellRing, Shield } from 'lucide-react';
import { generateCover as generateCoverImage } from '../utils/generateCover';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Textarea } from '../components/ui/Textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Progress } from '../components/ui/Progress';
import { Separator } from '../components/ui/Separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/Select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import { ScrollArea } from '../components/ui/ScrollArea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/AlertDialog';
import { Switch } from '../components/ui/Switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/Tooltip';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { playNotificationSound, playRingtone } from '../utils/notificationSound';
import { requestNotificationPermission, showWebNotification } from '../utils/pwa';
import { toast } from 'sonner';
import type { User } from '../contexts/AuthContext';
type Section = 'dashboard' | 'upload' | 'manage' | 'payments' | 'users' | 'chat' | 'settings';
const SUBJECTS = ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'Kinyarwanda', 'French', 'History', 'Geography', 'Economics', 'Entrepreneurship', 'Computer Science', 'General Studies'];
const LEVELS = ['S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'Primary', 'University'];
export function AdminDashboard() {
  const {
    t
  } = useLanguage();
  const {
    isAdmin,
    logout,
    getUsers,
    grantAccess
  } = useAuth();
  const {
    files,
    payments,
    notifications,
    messages,
    reviews,
    discountCodes,
    addFile,
    removeFile,
    storeFileData,
    getFileData,
    verifyPayment,
    denyPayment,
    sendMessage,
    getConversationMessages,
    getConversations,
    markNotificationRead,
    markAllNotificationsRead,
    getUnreadNotificationCount,
    addDiscountCode
  } = useData();
  const navigate = useNavigate();
  const [section, setSection] = useState<Section>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 768);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(() => {
    return typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted';
  });
  const [showNotifications, setShowNotifications] = useState(false);
  const prevNotifCount = useRef(0);
  // Users state (async from Firebase)
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  // Upload state
  const [uploadName, setUploadName] = useState('');
  const [uploadType, setUploadType] = useState<string>('book');
  const [uploadCover, setUploadCover] = useState('');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploadPrice, setUploadPrice] = useState('');
  const [uploadFormat, setUploadFormat] = useState('PDF');
  const [uploadSubject, setUploadSubject] = useState('');
  const [uploadLevel, setUploadLevel] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState<globalThis.File | null>(null);
  const [uploadFileData, setUploadFileData] = useState<string | null>(null);
  const [coverFile, setCoverFile] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  // Discount code state
  const [newDiscountCode, setNewDiscountCode] = useState('');
  const [newDiscountPercent, setNewDiscountPercent] = useState('');
  const [newDiscountMaxUses, setNewDiscountMaxUses] = useState('100');
  // Chat state
  const [selectedConvo, setSelectedConvo] = useState<string | null>(null);
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isAdmin) navigate('/admin/login');
  }, [isAdmin, navigate]);
  // Fetch users from Firebase
  useEffect(() => {
    if (!isAdmin) return;
    setUsersLoading(true);
    getUsers().then(users => {
      setAllUsers(users);
      setUsersLoading(false);
    }).catch(() => {
      setUsersLoading(false);
    });
  }, [isAdmin, getUsers, payments]);
  // Request notification permission on mount
  useEffect(() => {
    if (isAdmin) {
      requestNotificationPermission().then(granted => {
        setPushEnabled(granted);
      });
    }
  }, [isAdmin]);
  // Enhanced notification sound + web notification
  useEffect(() => {
    const count = getUnreadNotificationCount();
    if (count > prevNotifCount.current && isAdmin) {
      const latestNotif = [...notifications].filter(n => !n.read).pop();
      if (soundEnabled) {
        if (latestNotif?.type === 'payment_request') {
          playRingtone();
        } else {
          playNotificationSound();
        }
      }
      if (pushEnabled && latestNotif) {
        showWebNotification('Theo EduMarket Rwanda', latestNotif.message);
      }
    }
    prevNotifCount.current = count;
  }, [notifications, soundEnabled, pushEnabled, isAdmin, getUnreadNotificationCount]);
  // Remove the old localStorage polling — Firestore listeners handle real-time updates
  const unreadCount = getUnreadNotificationCount();
  const pendingPayments = payments.filter(p => p.status === 'pending');
  const verifiedPayments = payments.filter(p => p.status === 'verified');
  const totalRevenue = verifiedPayments.reduce((sum, p) => sum + p.amount, 0);
  const conversations = getConversations();
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadFile(file);
    if (!uploadName) setUploadName(file.name.replace(/\.[^/.]+$/, ''));
    const ext = file.name.split('.').pop()?.toUpperCase() || 'PDF';
    setUploadFormat(ext);
    const reader = new FileReader();
    reader.onload = ev => {
      setUploadFileData(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  };
  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      setCoverFile(ev.target?.result as string);
      setUploadCover(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  };
  const handleUpload = async () => {
    if (!uploadName || !uploadDesc || !uploadPrice) {
      toast.error(t('auth.error.required'));
      return;
    }
    if (!uploadFile && !uploadFileData) {
      toast.error('Please select a file to upload');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    for (let i = 0; i <= 100; i += 2) {
      await new Promise(r => setTimeout(r, 40));
      setUploadProgress(i);
    }
    const fileId = 'file_' + Date.now();
    let storedData = false;
    if (uploadFileData) {
      try {
        await storeFileData(fileId, uploadFileData);
        storedData = true;
      } catch (e) {
        console.warn('File storage failed, trying localStorage only');
        try {
          localStorage.setItem(`edumarket_filedata_${fileId}`, uploadFileData);
          storedData = true;
        } catch {
          toast.info('File is large — download will be simulated');
        }
      }
    }
    await addFile({
      id: fileId,
      name: uploadName,
      type: uploadType as 'book' | 'pastpaper' | 'other',
      coverUrl: uploadCover || `https://picsum.photos/seed/${fileId}/400/300`,
      description: uploadDesc,
      price: parseInt(uploadPrice) || 0,
      fileFormat: uploadFormat,
      fileSize: uploadFile?.size || 0,
      hasFileData: storedData,
      subject: uploadSubject || undefined,
      level: uploadLevel || undefined
    });
    toast.success(t('admin.upload.success'));
    setUploadName('');
    setUploadType('book');
    setUploadCover('');
    setUploadDesc('');
    setUploadPrice('');
    setUploadFormat('PDF');
    setUploadSubject('');
    setUploadLevel('');
    setUploadFile(null);
    setUploadFileData(null);
    setCoverFile(null);
    setUploading(false);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (coverInputRef.current) coverInputRef.current.value = '';
  };
  const handleVerify = (paymentId: string) => {
    verifyPayment(paymentId);
    const payment = payments.find(p => p.id === paymentId);
    if (payment) grantAccess(payment.userId, payment.fileId);
    toast.success('Payment verified!');
  };
  const handleDeny = (paymentId: string) => {
    denyPayment(paymentId);
    toast.error('Payment denied');
  };
  const handleSendChat = (conversationId: string) => {
    if (!chatInput.trim()) return;
    const convo = conversations.find(c => c.userId === conversationId.replace('conv_', ''));
    sendMessage({
      senderId: 'admin',
      senderName: 'BAYAVUGE THEOGEN',
      senderType: 'admin',
      receiverId: conversationId.replace('conv_', ''),
      message: chatInput.trim(),
      conversationId
    });
    setChatInput('');
    setTimeout(() => chatEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    }), 100);
  };
  const sidebarItems: {
    key: Section;
    icon: React.ElementType;
    label: string;
    badge?: number;
  }[] = [{
    key: 'dashboard',
    icon: LayoutDashboard,
    label: t('admin.dashboard')
  }, {
    key: 'upload',
    icon: Upload,
    label: t('admin.upload')
  }, {
    key: 'manage',
    icon: FolderOpen,
    label: t('admin.manage'),
    badge: files.length
  }, {
    key: 'payments',
    icon: CreditCard,
    label: t('admin.payments'),
    badge: pendingPayments.length
  }, {
    key: 'users',
    icon: Users,
    label: t('admin.users'),
    badge: allUsers.length
  }, {
    key: 'chat',
    icon: MessageCircle,
    label: t('admin.chat')
  }, {
    key: 'settings',
    icon: Settings,
    label: t('admin.settings')
  }];
  if (!isAdmin) return null;
  return <TooltipProvider data-id="element-190">
      <div className="flex h-[calc(100vh-64px)] overflow-hidden" data-id="element-191">
        {/* Sidebar */}
        <motion.aside animate={{
        width: sidebarOpen ? 240 : 64
      }} className="border-r border-border bg-card flex flex-col flex-shrink-0 overflow-hidden" data-id="element-192">
          <div className="p-3 flex items-center justify-between" data-id="element-193">
            {sidebarOpen && <span className="font-heading font-bold text-sm truncate" data-id="element-194">
                Theo Admin
              </span>}
            <Button variant="ghost" size="icon-xs" onClick={() => setSidebarOpen(!sidebarOpen)} data-id="element-195">
              {sidebarOpen ? <ChevronLeft className="h-4 w-4" data-id="element-196" /> : <ChevronRight className="h-4 w-4" data-id="element-197" />}
            </Button>
          </div>
          <Separator data-id="element-198" />
          <nav className="flex-1 p-2 space-y-1" data-id="element-199">
            {sidebarItems.map(item => <Tooltip key={item.key} data-id="element-200">
                <TooltipTrigger asChild data-id="element-201">
                  <button onClick={() => setSection(item.key)} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${section === item.key ? 'bg-blue-600 text-white' : 'hover:bg-muted text-muted-foreground hover:text-foreground'}`} data-id="element-202">
                    <item.icon className="h-4 w-4 flex-shrink-0" data-id="element-203" />
                    {sidebarOpen && <>
                        <span className="truncate flex-1 text-left" data-id="element-204">
                          {item.label}
                        </span>
                        {item.badge !== undefined && item.badge > 0 && <Badge variant="secondary" className="text-xs h-5 px-1.5" data-id="element-205">
                            {item.badge}
                          </Badge>}
                      </>}
                  </button>
                </TooltipTrigger>
                {!sidebarOpen && <TooltipContent side="right" data-id="element-206">{item.label}</TooltipContent>}
              </Tooltip>)}
          </nav>
          <Separator data-id="element-207" />
          <div className="p-2" data-id="element-208">
            <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-red-500" onClick={() => {
            logout();
            navigate('/');
          }} data-id="element-209">
              <LogOut className="h-4 w-4" data-id="element-210" />
              {sidebarOpen && t('nav.logout')}
            </Button>
          </div>
        </motion.aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden" data-id="element-211">
          {/* Top Bar */}
          <header className="h-14 border-b border-border bg-card flex items-center justify-between px-4 flex-shrink-0" data-id="element-212">
            <h2 className="font-heading font-semibold" data-id="element-213">
              {sidebarItems.find(s => s.key === section)?.label}
            </h2>
            <div className="flex items-center gap-2" data-id="element-214">
              {/* Notification Bell */}
              <div className="relative" data-id="element-215">
                <Button variant="ghost" size="icon-sm" onClick={() => setShowNotifications(!showNotifications)} className="relative" data-id="element-216">
                  <motion.div animate={unreadCount > 0 ? {
                  rotate: [0, -15, 15, -10, 10, 0]
                } : {}} transition={{
                  duration: 0.5,
                  repeat: unreadCount > 0 ? Infinity : 0,
                  repeatDelay: 3
                }} data-id="element-217">
                    <Bell className="h-4 w-4" data-id="element-218" />
                  </motion.div>
                  {unreadCount > 0 && <motion.span initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-bold" data-id="element-219">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </motion.span>}
                </Button>

                {/* Notification Dropdown */}
                <AnimatePresence data-id="element-220">
                  {showNotifications && <motion.div initial={{
                  opacity: 0,
                  y: -10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-lg shadow-xl z-50" data-id="element-221">
                      <div className="p-3 flex items-center justify-between border-b border-border" data-id="element-222">
                        <span className="font-semibold text-sm" data-id="element-223">
                          {t('admin.notifications')}
                        </span>
                        <Button variant="ghost" size="xs" onClick={() => {
                      markAllNotificationsRead();
                    }} data-id="element-224">
                          Mark all read
                        </Button>
                      </div>
                      <ScrollArea className="max-h-64" data-id="element-225">
                        {notifications.length === 0 ? <p className="p-4 text-sm text-muted-foreground text-center" data-id="element-226">
                            No notifications
                          </p> : [...notifications].reverse().slice(0, 20).map(n => <div key={n.id} onClick={() => markNotificationRead(n.id)} className={`p-3 border-b border-border text-xs cursor-pointer hover:bg-muted transition-colors ${!n.read ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`} data-id="element-227">
                                <div className="flex items-start gap-2" data-id="element-228">
                                  {!n.read && <div className="h-2 w-2 rounded-full bg-blue-600 mt-1 flex-shrink-0" data-id="element-229" />}
                                  <div className="flex-1" data-id="element-230">
                                    <p className={!n.read ? 'font-medium' : ''} data-id="element-231">
                                      {n.message}
                                    </p>
                                    <p className="text-muted-foreground mt-1" data-id="element-232">
                                      {new Date(n.createdAt).toLocaleString()}
                                    </p>
                                  </div>
                                </div>
                              </div>)}
                      </ScrollArea>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </header>

          {/* Content */}
          <ScrollArea className="flex-1" data-id="element-233">
            <div className="p-6" data-id="element-234">
              <AnimatePresence mode="wait" data-id="element-235">
                {/* DASHBOARD */}
                {section === 'dashboard' && <motion.div key="dashboard" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="space-y-6" data-id="element-236">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-237">
                      {[{
                    label: t('admin.stats.files'),
                    value: files.length,
                    icon: FileText,
                    color: 'text-blue-600'
                  }, {
                    label: t('admin.stats.users'),
                    value: allUsers.length,
                    icon: Users,
                    color: 'text-green-600'
                  }, {
                    label: t('admin.stats.pending'),
                    value: pendingPayments.length,
                    icon: CreditCard,
                    color: 'text-yellow-600'
                  }, {
                    label: t('admin.stats.revenue'),
                    value: `${totalRevenue.toLocaleString()} FRW`,
                    icon: CheckCircle,
                    color: 'text-emerald-600'
                  }].map((stat, i) => <motion.div key={i} initial={{
                    opacity: 0,
                    y: 20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: i * 0.1
                  }} data-id="element-238">
                          <Card data-id="element-239">
                            <CardContent className="p-4 flex items-center gap-4" data-id="element-240">
                              <div className={`h-10 w-10 rounded-lg bg-muted flex items-center justify-center ${stat.color}`} data-id="element-241">
                                <stat.icon className="h-5 w-5" data-id="element-242" />
                              </div>
                              <div data-id="element-243">
                                <p className="text-xs text-muted-foreground" data-id="element-244">
                                  {stat.label}
                                </p>
                                <p className="text-xl font-bold" data-id="element-245">
                                  {stat.value}
                                </p>
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>)}
                    </div>
                    {pendingPayments.length > 0 && <Card data-id="element-246">
                        <CardHeader data-id="element-247">
                          <CardTitle className="text-base" data-id="element-248">
                            Recent Pending Payments
                          </CardTitle>
                        </CardHeader>
                        <CardContent data-id="element-249">
                          {pendingPayments.slice(0, 5).map(p => <div key={p.id} className="flex items-center justify-between py-2 border-b border-border last:border-0" data-id="element-250">
                              <div data-id="element-251">
                                <p className="text-sm font-medium" data-id="element-252">
                                  {p.payerName}
                                </p>
                                <p className="text-xs text-muted-foreground" data-id="element-253">
                                  {p.fileName} — {p.amount} FRW
                                </p>
                              </div>
                              <div className="flex gap-2" data-id="element-254">
                                <Button size="xs" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleVerify(p.id)} data-id="element-255">
                                  <CheckCircle className="h-3 w-3" data-id="element-256" />
                                </Button>
                                <Button size="xs" variant="destructive" onClick={() => handleDeny(p.id)} data-id="element-257">
                                  <XCircle className="h-3 w-3" data-id="element-258" />
                                </Button>
                              </div>
                            </div>)}
                        </CardContent>
                      </Card>}
                  </motion.div>}

                {/* UPLOAD */}
                {section === 'upload' && <motion.div key="upload" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="max-w-2xl space-y-6" data-id="element-259">
                    {/* File Dropzone */}
                    <Card data-id="element-260">
                      <CardHeader data-id="element-261">
                        <CardTitle className="flex items-center gap-2" data-id="element-262">
                          <Upload className="h-5 w-5 text-blue-600" data-id="element-263" />{' '}
                          {t('admin.upload')}
                        </CardTitle>
                        <CardDescription data-id="element-264">
                          Select a file from your device to upload
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4" data-id="element-265">
                        {/* Hidden file inputs */}
                        <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.txt,.jpg,.png" onChange={handleFileSelect} data-id="element-266" />
                        <input ref={coverInputRef} type="file" className="hidden" accept="image/*" onChange={handleCoverSelect} data-id="element-267" />

                        {/* File Dropzone Area */}
                        <motion.button type="button" whileHover={{
                      scale: 1.01
                    }} whileTap={{
                      scale: 0.99
                    }} onClick={() => fileInputRef.current?.click()} className={`w-full border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${uploadFile ? 'border-green-500 bg-green-50 dark:bg-green-900/10' : 'border-border hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/10'}`} data-id="element-268">
                          {uploadFile ? <div className="flex flex-col items-center gap-3" data-id="element-269">
                              <div className="h-14 w-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-270">
                                <FileText className="h-7 w-7 text-green-600" data-id="element-271" />
                              </div>
                              <div data-id="element-272">
                                <p className="font-semibold text-sm" data-id="element-273">
                                  {uploadFile.name}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1" data-id="element-274">
                                  {(uploadFile.size / 1024).toFixed(1)} KB •{' '}
                                  {uploadFormat}
                                </p>
                              </div>
                              <div className="flex items-center gap-2" data-id="element-275">
                                <Badge className="bg-green-600 text-white gap-1" data-id="element-276">
                                  <CheckCircle className="h-3 w-3" data-id="element-277" /> File
                                  Selected
                                </Badge>
                                <Button variant="ghost" size="xs" className="text-red-500 hover:text-red-600" onClick={e => {
                            e.stopPropagation();
                            setUploadFile(null);
                            setUploadFileData(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }} data-id="element-278">
                                  <X className="h-3 w-3 mr-1" data-id="element-279" /> Remove
                                </Button>
                              </div>
                            </div> : <div className="flex flex-col items-center gap-3" data-id="element-280">
                              <motion.div animate={{
                          y: [0, -5, 0]
                        }} transition={{
                          duration: 2,
                          repeat: Infinity
                        }} className="h-16 w-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center" data-id="element-281">
                                <Upload className="h-8 w-8 text-blue-600" data-id="element-282" />
                              </motion.div>
                              <div data-id="element-283">
                                <p className="font-semibold text-sm" data-id="element-284">
                                  Click to select a file
                                </p>
                                <p className="text-xs text-muted-foreground mt-1" data-id="element-285">
                                  PDF, DOCX, XLSX, PPT, ZIP, Images and more
                                </p>
                              </div>
                              <Badge variant="outline" className="gap-1" data-id="element-286">
                                <HardDrive className="h-3 w-3" data-id="element-287" /> Browse File
                                Manager
                              </Badge>
                            </div>}
                        </motion.button>

                        <Separator data-id="element-288" />

                        {/* Cover Image */}
                        <div className="space-y-3" data-id="element-289">
                          <Label className="flex items-center gap-2" data-id="element-290">
                            <Image className="h-3.5 w-3.5" data-id="element-291" />{' '}
                            {t('admin.upload.cover')}
                          </Label>
                          <div className="flex flex-wrap gap-2" data-id="element-292">
                            <Button variant="outline" size="sm" className="gap-2" onClick={() => coverInputRef.current?.click()} data-id="element-293">
                              <Image className="h-4 w-4" data-id="element-294" />
                              Upload Cover
                            </Button>
                            <Button variant="outline" size="sm" className="gap-2 border-blue-300 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20" onClick={() => {
                          const name = uploadName || 'Untitled File';
                          const generated = generateCoverImage(name, uploadType, uploadFormat);
                          setCoverFile(generated);
                          setUploadCover(generated);
                          toast.success('Cover generated!');
                        }} data-id="element-295">
                              <Wand2 className="h-4 w-4" data-id="element-296" />
                              Generate Cover
                            </Button>
                          </div>
                          {/* Cover Preview */}
                          {(coverFile || uploadCover) && <motion.div initial={{
                        opacity: 0,
                        scale: 0.95
                      }} animate={{
                        opacity: 1,
                        scale: 1
                      }} className="relative inline-block" data-id="element-297">
                              <img src={coverFile || uploadCover} alt="Cover preview" className="h-32 w-auto rounded-lg object-cover border shadow-sm" data-id="element-298" />
                              <Button variant="ghost" size="icon-xs" className="absolute -top-2 -right-2 bg-red-500 text-white hover:bg-red-600 rounded-full h-5 w-5" onClick={() => {
                          setCoverFile(null);
                          setUploadCover('');
                          if (coverInputRef.current) coverInputRef.current.value = '';
                        }} data-id="element-299">
                                <X className="h-3 w-3" data-id="element-300" />
                              </Button>
                            </motion.div>}
                          <p className="text-xs text-muted-foreground" data-id="element-301">
                            Upload an image, generate one automatically, or
                            paste a URL below
                          </p>
                          <Input value={!coverFile ? uploadCover : ''} onChange={e => {
                        setUploadCover(e.target.value);
                        setCoverFile(null);
                      }} placeholder="https://... (optional)" disabled={!!coverFile} data-id="element-302" />
                        </div>

                        <Separator data-id="element-303" />

                        {/* File Details */}
                        <div className="space-y-4" data-id="element-304">
                          <div className="space-y-2" data-id="element-305">
                            <Label data-id="element-306">{t('admin.upload.name')}</Label>
                            <Input value={uploadName} onChange={e => setUploadName(e.target.value)} placeholder="e.g. Mathematics Past Paper 2024" data-id="element-307" />
                          </div>
                          <div className="grid grid-cols-2 gap-4" data-id="element-308">
                            <div className="space-y-2" data-id="element-309">
                              <Label data-id="element-310">{t('admin.upload.type')}</Label>
                              <Select value={uploadType} onValueChange={setUploadType} data-id="element-311">
                                <SelectTrigger data-id="element-312">
                                  <SelectValue data-id="element-313" />
                                </SelectTrigger>
                                <SelectContent data-id="element-314">
                                  <SelectItem value="book" data-id="element-315">📚 Book</SelectItem>
                                  <SelectItem value="pastpaper" data-id="element-316">
                                    📝 Past Paper
                                  </SelectItem>
                                  <SelectItem value="other" data-id="element-317">
                                    📁 Other
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2" data-id="element-318">
                              <Label data-id="element-319">File Format</Label>
                              <Select value={uploadFormat} onValueChange={setUploadFormat} data-id="element-320">
                                <SelectTrigger data-id="element-321">
                                  <SelectValue data-id="element-322" />
                                </SelectTrigger>
                                <SelectContent data-id="element-323">
                                  <SelectItem value="PDF" data-id="element-324">PDF</SelectItem>
                                  <SelectItem value="DOCX" data-id="element-325">DOCX</SelectItem>
                                  <SelectItem value="XLSX" data-id="element-326">XLSX</SelectItem>
                                  <SelectItem value="ZIP" data-id="element-327">ZIP</SelectItem>
                                  <SelectItem value="PPT" data-id="element-328">PPT</SelectItem>
                                  <SelectItem value="JPG" data-id="element-329">JPG</SelectItem>
                                  <SelectItem value="PNG" data-id="element-330">PNG</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4" data-id="element-331">
                            <div className="space-y-2" data-id="element-332">
                              <Label data-id="element-333">📖 Subject (optional)</Label>
                              <Select value={uploadSubject} onValueChange={setUploadSubject} data-id="element-334">
                                <SelectTrigger data-id="element-335">
                                  <SelectValue placeholder="Select subject" data-id="element-336" />
                                </SelectTrigger>
                                <SelectContent data-id="element-337">
                                  <SelectItem value="" data-id="element-338">None</SelectItem>
                                  {SUBJECTS.map(s => <SelectItem key={s} value={s} data-id="element-339">
                                      {s}
                                    </SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2" data-id="element-340">
                              <Label data-id="element-341">🎓 Level (optional)</Label>
                              <Select value={uploadLevel} onValueChange={setUploadLevel} data-id="element-342">
                                <SelectTrigger data-id="element-343">
                                  <SelectValue placeholder="Select level" data-id="element-344" />
                                </SelectTrigger>
                                <SelectContent data-id="element-345">
                                  <SelectItem value="" data-id="element-346">None</SelectItem>
                                  {LEVELS.map(l => <SelectItem key={l} value={l} data-id="element-347">
                                      {l}
                                    </SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="space-y-2" data-id="element-348">
                            <Label data-id="element-349">{t('admin.upload.desc')}</Label>
                            <Textarea value={uploadDesc} onChange={e => setUploadDesc(e.target.value)} placeholder="Describe this material..." rows={3} data-id="element-350" />
                          </div>
                          <div className="space-y-2" data-id="element-351">
                            <Label data-id="element-352">{t('admin.upload.price')}</Label>
                            <div className="relative" data-id="element-353">
                              <Input type="number" value={uploadPrice} onChange={e => setUploadPrice(e.target.value)} placeholder="e.g. 500" className="pr-14" data-id="element-354" />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-medium" data-id="element-355">
                                FRW
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Upload Progress */}
                        {uploading && <motion.div initial={{
                      opacity: 0,
                      y: 10
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} className="space-y-2 bg-blue-50 dark:bg-blue-900/10 rounded-lg p-4" data-id="element-356">
                            <div className="flex justify-between text-xs font-medium" data-id="element-357">
                              <span className="flex items-center gap-2" data-id="element-358">
                                <motion.div animate={{
                            rotate: 360
                          }} transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: 'linear'
                          }} data-id="element-359">
                                  <Upload className="h-3.5 w-3.5 text-blue-600" data-id="element-360" />
                                </motion.div>
                                Uploading {uploadFile?.name || 'file'}...
                              </span>
                              <span className="text-blue-600" data-id="element-361">
                                {uploadProgress}%
                              </span>
                            </div>
                            <Progress value={uploadProgress} className="h-2" data-id="element-362" />
                            {uploadProgress === 100 && <motion.p initial={{
                        opacity: 0
                      }} animate={{
                        opacity: 1
                      }} className="text-xs text-green-600 font-medium flex items-center gap-1" data-id="element-363">
                                <CheckCircle className="h-3 w-3" data-id="element-364" /> Upload
                                complete! Hosting file...
                              </motion.p>}
                          </motion.div>}

                        {/* Submit Button */}
                        <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2 h-12 text-base" onClick={handleUpload} disabled={uploading} data-id="element-365">
                          {uploading ? <>
                              <motion.div animate={{
                          rotate: 360
                        }} transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: 'linear'
                        }} data-id="element-366">
                                <Upload className="h-5 w-5" data-id="element-367" />
                              </motion.div>
                              Uploading...
                            </> : <>
                              <Upload className="h-5 w-5" data-id="element-368" />
                              {t('admin.upload.btn')}
                            </>}
                        </Button>

                        {/* Storage Info */}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-lg p-3" data-id="element-369">
                          <HardDrive className="h-3.5 w-3.5 flex-shrink-0" data-id="element-370" />
                          <span data-id="element-371">
                            Files are stored in Firebase cloud database. Small
                            files (under 900KB) are fully stored; larger files
                            are cached locally.
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>}

                {/* MANAGE FILES */}
                {section === 'manage' && <motion.div key="manage" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="space-y-4" data-id="element-372">
                    {/* Storage summary */}
                    <div className="flex items-center gap-4 flex-wrap" data-id="element-373">
                      <Badge variant="secondary" className="gap-1.5 py-1 px-3" data-id="element-374">
                        <FolderOpen className="h-3.5 w-3.5" data-id="element-375" />
                        {files.length} files hosted
                      </Badge>
                      <Badge variant="outline" className="gap-1.5 py-1 px-3" data-id="element-376">
                        <HardDrive className="h-3.5 w-3.5" data-id="element-377" />
                        {files.filter(f => f.hasFileData).length} with stored
                        data
                      </Badge>
                    </div>

                    <Card data-id="element-378">
                      <CardHeader data-id="element-379">
                        <CardTitle className="flex items-center gap-2" data-id="element-380">
                          <FolderOpen className="h-5 w-5" data-id="element-381" />
                          {t('admin.manage')} ({files.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent data-id="element-382">
                        {files.length === 0 ? <div className="text-center py-12" data-id="element-383">
                            <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-3" data-id="element-384" />
                            <p className="text-muted-foreground text-sm" data-id="element-385">
                              No files uploaded yet
                            </p>
                            <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={() => setSection('upload')} data-id="element-386">
                              <Upload className="h-4 w-4" data-id="element-387" /> Upload First File
                            </Button>
                          </div> : <Table data-id="element-388">
                            <TableHeader data-id="element-389">
                              <TableRow data-id="element-390">
                                <TableHead data-id="element-391">Cover</TableHead>
                                <TableHead data-id="element-392">Name</TableHead>
                                <TableHead data-id="element-393">Type</TableHead>
                                <TableHead data-id="element-394">Price</TableHead>
                                <TableHead data-id="element-395">Format</TableHead>
                                <TableHead data-id="element-396">Size</TableHead>
                                <TableHead className="text-right" data-id="element-397">
                                  Actions
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody data-id="element-398">
                              {files.map(file => <TableRow key={file.id} data-id="element-399">
                                  <TableCell data-id="element-400">
                                    <img src={file.coverUrl} alt="" className="h-10 w-10 rounded object-cover bg-muted" onError={e => {
                              ;
                              (e.target as HTMLImageElement).src = `https://picsum.photos/seed/${file.id}/40/40`;
                            }} data-id="element-401" />
                                  </TableCell>
                                  <TableCell className="font-medium max-w-[200px]" data-id="element-402">
                                    <p className="truncate" data-id="element-403">{file.name}</p>
                                    {file.hasFileData && <span className="text-[10px] text-green-600 flex items-center gap-0.5 mt-0.5" data-id="element-404">
                                        <CheckCircle className="h-2.5 w-2.5" data-id="element-405" />{' '}
                                        Stored
                                      </span>}
                                  </TableCell>
                                  <TableCell data-id="element-406">
                                    <Badge variant="secondary" data-id="element-407">
                                      {file.type}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="font-mono text-sm" data-id="element-408">
                                    {file.price.toLocaleString()} FRW
                                  </TableCell>
                                  <TableCell data-id="element-409">
                                    <Badge variant="outline" data-id="element-410">
                                      {file.fileFormat}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-xs text-muted-foreground" data-id="element-411">
                                    {file.fileSize ? `${(file.fileSize / 1024).toFixed(1)} KB` : '—'}
                                  </TableCell>
                                  <TableCell className="text-right" data-id="element-412">
                                    <div className="flex justify-end gap-1" data-id="element-413">
                                      <Tooltip data-id="element-414">
                                        <TooltipTrigger asChild data-id="element-415">
                                          <Button variant="ghost" size="icon-xs" className="text-blue-600" onClick={async () => {
                                    toast.info(`Preparing ${file.name}...`);
                                    const data = await getFileData(file.id);
                                    if (data) {
                                      const a = document.createElement('a');
                                      a.href = data;
                                      a.download = `${file.name}.${file.fileFormat.toLowerCase()}`;
                                      document.body.appendChild(a);
                                      a.click();
                                      document.body.removeChild(a);
                                      toast.success(`Downloading ${file.name}`);
                                    } else {
                                      const blob = new Blob([`${file.name}\nFormat: ${file.fileFormat}\nPrice: ${file.price} FRW\n\nThis is a simulated file from Theo EduMarket Rwanda.`], {
                                        type: 'text/plain'
                                      });
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = `${file.name}.${file.fileFormat.toLowerCase()}`;
                                      document.body.appendChild(a);
                                      a.click();
                                      document.body.removeChild(a);
                                      URL.revokeObjectURL(url);
                                      toast.success(`Downloading ${file.name}`);
                                    }
                                  }} data-id="element-416">
                                            <Download className="h-3.5 w-3.5" data-id="element-417" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent data-id="element-418">
                                          Download
                                        </TooltipContent>
                                      </Tooltip>
                                      <AlertDialog data-id="element-419">
                                        <AlertDialogTrigger asChild data-id="element-420">
                                          <Button variant="ghost" size="icon-xs" className="text-red-500 hover:text-red-600" data-id="element-421">
                                            <Trash2 className="h-3.5 w-3.5" data-id="element-422" />
                                          </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent data-id="element-423">
                                          <AlertDialogHeader data-id="element-424">
                                            <AlertDialogTitle data-id="element-425">
                                              Delete "{file.name}"?
                                            </AlertDialogTitle>
                                            <AlertDialogDescription data-id="element-426">
                                              This action cannot be undone. The
                                              file will be permanently removed.
                                            </AlertDialogDescription>
                                          </AlertDialogHeader>
                                          <AlertDialogFooter data-id="element-427">
                                            <AlertDialogCancel data-id="element-428">
                                              {t('common.cancel')}
                                            </AlertDialogCancel>
                                            <AlertDialogAction onClick={() => {
                                      removeFile(file.id);
                                      localStorage.removeItem(`edumarket_filedata_${file.id}`);
                                      toast.success('File deleted');
                                    }} className="bg-red-600 hover:bg-red-700" data-id="element-429">
                                              <Trash2 className="h-4 w-4 mr-1" data-id="element-430" />
                                              {t('admin.delete')}
                                            </AlertDialogAction>
                                          </AlertDialogFooter>
                                        </AlertDialogContent>
                                      </AlertDialog>
                                    </div>
                                  </TableCell>
                                </TableRow>)}
                            </TableBody>
                          </Table>}
                      </CardContent>
                    </Card>
                  </motion.div>}

                {/* PAYMENTS */}
                {section === 'payments' && <motion.div key="payments" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="space-y-4" data-id="element-431">
                    <Card data-id="element-432">
                      <CardHeader data-id="element-433">
                        <CardTitle data-id="element-434">
                          Pending Payments ({pendingPayments.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent data-id="element-435">
                        {pendingPayments.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8" data-id="element-436">
                            No pending payments
                          </p> : <div className="space-y-3" data-id="element-437">
                            {pendingPayments.map(p => <div key={p.id} className="border border-border rounded-lg p-4 space-y-3" data-id="element-438">
                                <div className="flex items-start justify-between" data-id="element-439">
                                  <div data-id="element-440">
                                    <p className="font-medium" data-id="element-441">{p.payerName}</p>
                                    <p className="text-sm text-muted-foreground" data-id="element-442">
                                      MTN: {p.payerMtnNumber}
                                    </p>
                                  </div>
                                  <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300" data-id="element-443">
                                    Pending
                                  </Badge>
                                </div>
                                <Separator data-id="element-444" />
                                <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-445">
                                  <div data-id="element-446">
                                    <span className="text-muted-foreground" data-id="element-447">
                                      File:
                                    </span>{' '}
                                    {p.fileName}
                                  </div>
                                  <div data-id="element-448">
                                    <span className="text-muted-foreground" data-id="element-449">
                                      Amount:
                                    </span>{' '}
                                    <strong data-id="element-450">
                                      {p.amount.toLocaleString()} FRW
                                    </strong>
                                  </div>
                                  <div data-id="element-451">
                                    <span className="text-muted-foreground" data-id="element-452">
                                      User:
                                    </span>{' '}
                                    {p.userName}
                                  </div>
                                  <div data-id="element-453">
                                    <span className="text-muted-foreground" data-id="element-454">
                                      Phone:
                                    </span>{' '}
                                    {p.userPhone}
                                  </div>
                                </div>
                                <p className="text-xs text-muted-foreground" data-id="element-455">
                                  Check if {p.payerName} ({p.payerMtnNumber})
                                  paid you {p.amount} FRW
                                </p>
                                <div className="flex gap-2" data-id="element-456">
                                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white gap-1 flex-1" onClick={() => handleVerify(p.id)} data-id="element-457">
                                    <CheckCircle className="h-3.5 w-3.5" data-id="element-458" />{' '}
                                    {t('admin.verify')}
                                  </Button>
                                  <Button size="sm" variant="destructive" className="gap-1 flex-1" onClick={() => handleDeny(p.id)} data-id="element-459">
                                    <XCircle className="h-3.5 w-3.5" data-id="element-460" />{' '}
                                    {t('admin.deny')}
                                  </Button>
                                </div>
                              </div>)}
                          </div>}
                      </CardContent>
                    </Card>
                    {verifiedPayments.length > 0 && <Card data-id="element-461">
                        <CardHeader data-id="element-462">
                          <CardTitle data-id="element-463">
                            Verified Payments ({verifiedPayments.length})
                          </CardTitle>
                        </CardHeader>
                        <CardContent data-id="element-464">
                          <Table data-id="element-465">
                            <TableHeader data-id="element-466">
                              <TableRow data-id="element-467">
                                <TableHead data-id="element-468">Payer</TableHead>
                                <TableHead data-id="element-469">File</TableHead>
                                <TableHead data-id="element-470">Amount</TableHead>
                                <TableHead data-id="element-471">Date</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody data-id="element-472">
                              {verifiedPayments.map(p => <TableRow key={p.id} data-id="element-473">
                                  <TableCell data-id="element-474">{p.payerName}</TableCell>
                                  <TableCell className="max-w-[150px] truncate" data-id="element-475">
                                    {p.fileName}
                                  </TableCell>
                                  <TableCell data-id="element-476">
                                    {p.amount.toLocaleString()} FRW
                                  </TableCell>
                                  <TableCell className="text-xs" data-id="element-477">
                                    {new Date(p.resolvedAt || p.createdAt).toLocaleDateString()}
                                  </TableCell>
                                </TableRow>)}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>}
                  </motion.div>}

                {/* USERS */}
                {section === 'users' && <motion.div key="users" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} data-id="element-478">
                    <Card data-id="element-479">
                      <CardHeader data-id="element-480">
                        <CardTitle data-id="element-481">
                          {t('admin.users')} ({allUsers.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent data-id="element-482">
                        {allUsers.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8" data-id="element-483">
                            No registered users yet
                          </p> : <Table data-id="element-484">
                            <TableHeader data-id="element-485">
                              <TableRow data-id="element-486">
                                <TableHead data-id="element-487">Name</TableHead>
                                <TableHead data-id="element-488">Phone</TableHead>
                                <TableHead data-id="element-489">Purchased</TableHead>
                                <TableHead data-id="element-490">Joined</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody data-id="element-491">
                              {allUsers.map(u => <TableRow key={u.id} data-id="element-492">
                                  <TableCell className="font-medium" data-id="element-493">
                                    {u.fullName}
                                  </TableCell>
                                  <TableCell data-id="element-494">
                                    {u.countryCode}
                                    {u.phone}
                                  </TableCell>
                                  <TableCell data-id="element-495">
                                    <Badge variant="secondary" data-id="element-496">
                                      {u.purchasedFiles.length} files
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-xs" data-id="element-497">
                                    {new Date(u.createdAt).toLocaleDateString()}
                                  </TableCell>
                                </TableRow>)}
                            </TableBody>
                          </Table>}
                      </CardContent>
                    </Card>
                  </motion.div>}

                {/* CHAT */}
                {section === 'chat' && <motion.div key="chat" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} data-id="element-498">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[500px]" data-id="element-499">
                      {/* Conversation List */}
                      <Card className="md:col-span-1" data-id="element-500">
                        <CardHeader className="py-3" data-id="element-501">
                          <CardTitle className="text-sm" data-id="element-502">
                            Conversations
                          </CardTitle>
                        </CardHeader>
                        <ScrollArea className="h-[420px]" data-id="element-503">
                          {conversations.length === 0 ? <p className="p-4 text-xs text-muted-foreground text-center" data-id="element-504">
                              No conversations
                            </p> : conversations.map(c => <button key={c.userId} onClick={() => setSelectedConvo('conv_' + c.userId)} className={`w-full text-left p-3 border-b border-border hover:bg-muted transition-colors ${selectedConvo === 'conv_' + c.userId ? 'bg-muted' : ''}`} data-id="element-505">
                                <p className="text-sm font-medium truncate" data-id="element-506">
                                  {c.userName}
                                </p>
                                <p className="text-xs text-muted-foreground truncate" data-id="element-507">
                                  {c.lastMessage}
                                </p>
                              </button>)}
                        </ScrollArea>
                      </Card>

                      {/* Chat Area */}
                      <Card className="md:col-span-2 flex flex-col" data-id="element-508">
                        {selectedConvo ? <>
                            <CardHeader className="py-3 border-b border-border" data-id="element-509">
                              <CardTitle className="text-sm" data-id="element-510">
                                {conversations.find(c => 'conv_' + c.userId === selectedConvo)?.userName || 'Chat'}
                              </CardTitle>
                            </CardHeader>
                            <ScrollArea className="flex-1 p-4" data-id="element-511">
                              <div className="space-y-3" data-id="element-512">
                                {getConversationMessages(selectedConvo).map(msg => <div key={msg.id} className={`flex ${msg.senderType === 'admin' ? 'justify-end' : 'justify-start'}`} data-id="element-513">
                                      <div className={`max-w-[70%] rounded-lg px-3 py-2 text-sm ${msg.senderType === 'admin' ? 'bg-blue-600 text-white' : 'bg-muted'}`} data-id="element-514">
                                        <p data-id="element-515">{msg.message}</p>
                                        {msg.attachment && <p className="text-xs mt-1 opacity-75" data-id="element-516">
                                            📎 {msg.attachment.name}
                                          </p>}
                                        <p className={`text-[10px] mt-1 ${msg.senderType === 'admin' ? 'text-blue-200' : 'text-muted-foreground'}`} data-id="element-517">
                                          {new Date(msg.createdAt).toLocaleTimeString()}
                                        </p>
                                      </div>
                                    </div>)}
                                <div ref={chatEndRef} data-id="element-518" />
                              </div>
                            </ScrollArea>
                            <div className="p-3 border-t border-border flex gap-2" data-id="element-519">
                              <Input value={chatInput} onChange={e => setChatInput(e.target.value)} placeholder="Type a message..." onKeyDown={e => e.key === 'Enter' && handleSendChat(selectedConvo)} className="flex-1" data-id="element-520" />
                              <Button size="icon" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => handleSendChat(selectedConvo)} data-id="element-521">
                                <Send className="h-4 w-4" data-id="element-522" />
                              </Button>
                            </div>
                          </> : <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm" data-id="element-523">
                            Select a conversation
                          </div>}
                      </Card>
                    </div>
                  </motion.div>}

                {/* SETTINGS */}
                {section === 'settings' && <motion.div key="settings" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} data-id="element-524">
                    <Card className="max-w-xl" data-id="element-525">
                      <CardHeader data-id="element-526">
                        <CardTitle data-id="element-527">{t('admin.settings')}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6" data-id="element-528">
                        <div className="space-y-2" data-id="element-529">
                          <h3 className="font-semibold text-sm" data-id="element-530">
                            Admin Profile
                          </h3>
                          <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-531">
                            <div className="text-muted-foreground" data-id="element-532">Name:</div>
                            <div className="font-medium" data-id="element-533">BAYAVUGE THEOGEN</div>
                            <div className="text-muted-foreground" data-id="element-534">Email:</div>
                            <div className="font-medium" data-id="element-535">
                              theobayavuge1@gmail.com
                            </div>
                            <div className="text-muted-foreground" data-id="element-536">MTN:</div>
                            <div className="font-medium" data-id="element-537">0783138128</div>
                            <div className="text-muted-foreground" data-id="element-538">
                              WhatsApp:
                            </div>
                            <div className="font-medium" data-id="element-539">0783138128</div>
                          </div>
                        </div>
                        <Separator data-id="element-540" />
                        <div className="flex items-center justify-between" data-id="element-541">
                          <div data-id="element-542">
                            <p className="text-sm font-medium flex items-center gap-2" data-id="element-543">
                              {soundEnabled ? <Volume2 className="h-4 w-4" data-id="element-544" /> : <VolumeX className="h-4 w-4" data-id="element-545" />}
                              Notification Sound
                            </p>
                            <p className="text-xs text-muted-foreground" data-id="element-546">
                              Play sound for new notifications
                            </p>
                          </div>
                          <Switch checked={soundEnabled} onCheckedChange={setSoundEnabled} data-id="element-547" />
                        </div>
                        <Separator data-id="element-548" />
                        <div className="flex items-center justify-between" data-id="element-549">
                          <div data-id="element-550">
                            <p className="text-sm font-medium flex items-center gap-2" data-id="element-551">
                              <BellRing className="h-4 w-4" data-id="element-552" />
                              Push Notifications
                            </p>
                            <p className="text-xs text-muted-foreground" data-id="element-553">
                              Show system notifications on your device
                            </p>
                          </div>
                          <Switch checked={pushEnabled} onCheckedChange={async checked => {
                        if (checked) {
                          const granted = await requestNotificationPermission();
                          setPushEnabled(granted);
                          if (!granted) {
                            toast.error('Notification permission denied. Enable in browser settings.');
                          } else {
                            toast.success('Push notifications enabled!');
                          }
                        } else {
                          setPushEnabled(false);
                        }
                      }} data-id="element-554" />
                        </div>
                        <Separator data-id="element-555" />
                        <div className="flex items-center justify-between" data-id="element-556">
                          <div data-id="element-557">
                            <p className="text-sm font-medium flex items-center gap-2" data-id="element-558">
                              <Shield className="h-4 w-4" data-id="element-559" />
                              Admin Session
                            </p>
                            <p className="text-xs text-muted-foreground" data-id="element-560">
                              {localStorage.getItem('edumarket_admin_remember') ? 'Login saved — auto-login on next visit' : 'Not saved — use Remember Me at login'}
                            </p>
                          </div>
                          <Badge variant={localStorage.getItem('edumarket_admin_remember') ? 'default' : 'secondary'} className="text-xs" data-id="element-561">
                            {localStorage.getItem('edumarket_admin_remember') ? 'Saved' : 'Not Saved'}
                          </Badge>
                        </div>
                        <Separator data-id="element-562" />

                        {/* Discount Codes */}
                        <div className="space-y-3" data-id="element-563">
                          <h3 className="font-semibold text-sm" data-id="element-564">
                            🏷️ Discount Codes
                          </h3>
                          <div className="grid grid-cols-3 gap-2" data-id="element-565">
                            <Input value={newDiscountCode} onChange={e => setNewDiscountCode(e.target.value.toUpperCase())} placeholder="CODE" className="text-xs" data-id="element-566" />
                            <Input type="number" value={newDiscountPercent} onChange={e => setNewDiscountPercent(e.target.value)} placeholder="% off" className="text-xs" data-id="element-567" />
                            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs" onClick={async () => {
                          if (!newDiscountCode || !newDiscountPercent) {
                            toast.error('Enter code and percentage');
                            return;
                          }
                          await addDiscountCode({
                            code: newDiscountCode,
                            percentage: parseInt(newDiscountPercent) || 0,
                            active: true,
                            maxUses: parseInt(newDiscountMaxUses) || 100
                          });
                          toast.success(`Discount code ${newDiscountCode} created!`);
                          setNewDiscountCode('');
                          setNewDiscountPercent('');
                        }} data-id="element-568">
                              Add
                            </Button>
                          </div>
                          {discountCodes.length > 0 && <div className="space-y-1.5" data-id="element-569">
                              {discountCodes.map(dc => <div key={dc.id} className="flex items-center justify-between text-xs bg-muted/50 rounded-lg p-2" data-id="element-570">
                                  <div className="flex items-center gap-2" data-id="element-571">
                                    <Badge variant="outline" className="font-mono" data-id="element-572">
                                      {dc.code}
                                    </Badge>
                                    <span className="text-green-600 font-bold" data-id="element-573">
                                      {dc.percentage}% off
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2" data-id="element-574">
                                    <span className="text-muted-foreground" data-id="element-575">
                                      {dc.usageCount}/{dc.maxUses} used
                                    </span>
                                    <Badge variant={dc.active && dc.usageCount < dc.maxUses ? 'default' : 'secondary'} className="text-[10px]" data-id="element-576">
                                      {dc.active && dc.usageCount < dc.maxUses ? 'Active' : 'Expired'}
                                    </Badge>
                                  </div>
                                </div>)}
                            </div>}
                        </div>

                        <Separator data-id="element-577" />

                        {/* Analytics Summary */}
                        <div className="space-y-3" data-id="element-578">
                          <h3 className="font-semibold text-sm" data-id="element-579">
                            📊 Quick Analytics
                          </h3>
                          <div className="grid grid-cols-2 gap-3" data-id="element-580">
                            <div className="bg-muted/50 rounded-lg p-3 text-center" data-id="element-581">
                              <p className="text-2xl font-bold text-blue-600" data-id="element-582">
                                {files.length}
                              </p>
                              <p className="text-[10px] text-muted-foreground" data-id="element-583">
                                Total Files
                              </p>
                            </div>
                            <div className="bg-muted/50 rounded-lg p-3 text-center" data-id="element-584">
                              <p className="text-2xl font-bold text-green-600" data-id="element-585">
                                {allUsers.length}
                              </p>
                              <p className="text-[10px] text-muted-foreground" data-id="element-586">
                                Total Users
                              </p>
                            </div>
                            <div className="bg-muted/50 rounded-lg p-3 text-center" data-id="element-587">
                              <p className="text-2xl font-bold text-emerald-600" data-id="element-588">
                                {totalRevenue.toLocaleString()}
                              </p>
                              <p className="text-[10px] text-muted-foreground" data-id="element-589">
                                Revenue (FRW)
                              </p>
                            </div>
                            <div className="bg-muted/50 rounded-lg p-3 text-center" data-id="element-590">
                              <p className="text-2xl font-bold text-purple-600" data-id="element-591">
                                {reviews.length}
                              </p>
                              <p className="text-[10px] text-muted-foreground" data-id="element-592">
                                Reviews
                              </p>
                            </div>
                            <div className="bg-muted/50 rounded-lg p-3 text-center" data-id="element-593">
                              <p className="text-2xl font-bold text-yellow-600" data-id="element-594">
                                {pendingPayments.length}
                              </p>
                              <p className="text-[10px] text-muted-foreground" data-id="element-595">
                                Pending Payments
                              </p>
                            </div>
                            <div className="bg-muted/50 rounded-lg p-3 text-center" data-id="element-596">
                              <p className="text-2xl font-bold text-cyan-600" data-id="element-597">
                                {messages.length}
                              </p>
                              <p className="text-[10px] text-muted-foreground" data-id="element-598">
                                Messages
                              </p>
                            </div>
                          </div>
                          {/* Top selling files */}
                          {verifiedPayments.length > 0 && <div className="space-y-1.5" data-id="element-599">
                              <p className="text-xs font-medium text-muted-foreground" data-id="element-600">
                                🔥 Top Selling Files
                              </p>
                              {(() => {
                          const fileSales = new Map<string, {
                            name: string;
                            count: number;
                            revenue: number;
                          }>();
                          verifiedPayments.forEach(p => {
                            const existing = fileSales.get(p.fileId) || {
                              name: p.fileName,
                              count: 0,
                              revenue: 0
                            };
                            existing.count += 1;
                            existing.revenue += p.amount;
                            fileSales.set(p.fileId, existing);
                          });
                          return Array.from(fileSales.values()).sort((a, b) => b.count - a.count).slice(0, 5).map((item, i) => <div key={i} className="flex items-center justify-between text-xs bg-muted/30 rounded p-2" data-id="element-601">
                                      <span className="truncate flex-1" data-id="element-602">
                                        {i + 1}. {item.name}
                                      </span>
                                      <div className="flex items-center gap-2 flex-shrink-0" data-id="element-603">
                                        <Badge variant="secondary" className="text-[10px]" data-id="element-604">
                                          {item.count} sales
                                        </Badge>
                                        <span className="font-mono text-green-600" data-id="element-605">
                                          {item.revenue.toLocaleString()} FRW
                                        </span>
                                      </div>
                                    </div>);
                        })()}
                            </div>}
                        </div>

                        <Separator data-id="element-606" />
                        <div className="space-y-2" data-id="element-607">
                          <h3 className="font-semibold text-sm" data-id="element-608">Developer</h3>
                          <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-609">
                            <div className="text-muted-foreground" data-id="element-610">Email:</div>
                            <div data-id="element-611">bazatsindapaulin@gmail.com</div>
                            <div className="text-muted-foreground" data-id="element-612">Phone:</div>
                            <div data-id="element-613">0792694883</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </ScrollArea>
        </div>
      </div>
    </TooltipProvider>;
}